/* eslint-disable no-underscore-dangle */
import { AssetContentFormat } from "../api/model/snapshots/Asset";
import Asset, { TransformedAsset } from "../model/Asset";
import InspectLayer from "../model/InspectLayer";
import NamingConvention from "../model/NamingConvention";
import { DENSITY, PLATFORM } from "../enums";

import BasicRecord from "./BasicRecord";
import generateName from "./generateName";
import { getDensityScale } from "./legacy";

type AssetDensityScalePrefs = BasicRecord<number[]>;

const EXPORT_FORMAT_ORDER: Record<AssetContentFormat | string, number> = {
    [AssetContentFormat.Png]: 0,
    [AssetContentFormat.Jpg]: 1,
    [AssetContentFormat.Webp]: 2,
    [AssetContentFormat.Pdf]: 3,
    [AssetContentFormat.Svg]: 4,
    [AssetContentFormat.Avif]: 5
};

const SUPPORTED_FORMATS = Object.freeze({
    [PLATFORM.BASE]: [AssetContentFormat.Svg],
    [PLATFORM.ANDROID]: [
        AssetContentFormat.Png,
        AssetContentFormat.Jpg,
        AssetContentFormat.Svg,
        AssetContentFormat.Webp
    ],
    [PLATFORM.IOS]: [
        AssetContentFormat.Png,
        AssetContentFormat.Jpg,
        AssetContentFormat.Pdf,
        AssetContentFormat.Svg
    ],
    [PLATFORM.MACOS]: [
        AssetContentFormat.Png,
        AssetContentFormat.Jpg,
        AssetContentFormat.Pdf,
        AssetContentFormat.Svg
    ],
    [PLATFORM.WEB]: [
        AssetContentFormat.Png,
        AssetContentFormat.Jpg,
        AssetContentFormat.Svg,
        AssetContentFormat.Webp,
        AssetContentFormat.Avif
    ]
});

const SUPPORTED_DENSITY_SCALES = Object.freeze({
    [PLATFORM.BASE]: [DENSITY["_1x"]],
    [PLATFORM.ANDROID]: [DENSITY["MDPI"], DENSITY["HDPI"], DENSITY["XHDPI"], DENSITY["XXHDPI"], DENSITY["XXXHDPI"]],
    [PLATFORM.IOS]: [DENSITY["_1x"], DENSITY["_2x"], DENSITY["_3x"]],
    [PLATFORM.MACOS]: [DENSITY["_1x"], DENSITY["_2x"]],
    [PLATFORM.WEB]: [DENSITY["_1x"], DENSITY["_2x"], DENSITY["_3x"]]
});

const ASSET_FORMAT_TITLES = {
    [AssetContentFormat.Png]: "PNG",
    [AssetContentFormat.Jpg]: "JPG",
    [AssetContentFormat.Pdf]: "PDF",
    [AssetContentFormat.Svg]: "SVG",
    [AssetContentFormat.Webp]: "WebP",
    [AssetContentFormat.Avif]: "AVIF"
};

function getFormatType(format: AssetContentFormat): "vector" | "bitmap" {
    switch (format) {
        case AssetContentFormat.Pdf:
        case AssetContentFormat.Svg:
            return "vector";

        case AssetContentFormat.Jpg:
        case AssetContentFormat.Png:
        case AssetContentFormat.Webp:
        case AssetContentFormat.Avif:
        default:
            return "bitmap";
    }
}

function getSupportedDensityScales(platform: PLATFORM): number[] {
    return SUPPORTED_DENSITY_SCALES[platform];
}

function initAssetDensityScalePrefsIfNecessary(
    platform: PLATFORM, assetDensityScalePrefs: AssetDensityScalePrefs | null | undefined
): AssetDensityScalePrefs {
    if (!assetDensityScalePrefs?.[AssetContentFormat.Png]) {
        return {
            png: getSupportedDensityScales(platform)
        };
    }

    return assetDensityScalePrefs;
}

function isAssetDensityScaleSelected(
    assetDensityScalePrefs: AssetDensityScalePrefs, format: AssetContentFormat, assetDensityScale: number
): boolean {
    // Asset density scales prefs for png should be applied to all bitmap formats
    const assetDensityScalePref = getFormatType(format) === "bitmap"
        ? assetDensityScalePrefs[AssetContentFormat.Png]
        : assetDensityScalePrefs[format];

    return !assetDensityScalePref || assetDensityScalePref.includes(assetDensityScale);
}

function getOptimizedAssetsSelectedNames({
    showOptimizedJPG, showOptimizedPNG, showOptimizedSVG
}: { showOptimizedJPG: boolean; showOptimizedPNG: boolean; showOptimizedSVG: boolean; }): string[] {
    const selectedNames = [];

    if (showOptimizedSVG) {
        selectedNames.push("SVG");
    }

    if (showOptimizedPNG) {
        selectedNames.push("PNG");
    }

    if (showOptimizedJPG) {
        selectedNames.push("JPG");
    }

    return selectedNames;
}

function sortFormats(f1: AssetContentFormat | string, f2: AssetContentFormat | string): number {
    return EXPORT_FORMAT_ORDER[f1] - EXPORT_FORMAT_ORDER[f2];
}

function getDefaultNamingConvention(platform: PLATFORM): NamingConvention {
    if (platform === PLATFORM.ANDROID) {
        return NamingConvention.SnakeCase;
    }

    if (platform === PLATFORM.IOS || platform === PLATFORM.MACOS) {
        return NamingConvention.CamelCase;
    }

    return NamingConvention.KebabCase;
}

function formatName(
    asset: Pick<TransformedAsset, "displayName" | "layerName">,
    namingConvention: NamingConvention,
    platform: PLATFORM
): string {
    if (namingConvention === NamingConvention.Original) {
        return asset.layerName!;
    }

    let convention: NamingConvention = namingConvention;
    if (namingConvention === NamingConvention.Default) {
        convention = getDefaultNamingConvention(platform);
    }

    return generateName(asset.layerName!, convention);
}

function getExportName(
    asset: Pick<TransformedAsset, "localName" | "displayName" | "layerName">,
    namingConvention: NamingConvention,
    platform: PLATFORM
): string {
    if (asset.localName) {
        return asset.localName;
    }

    return formatName(asset, namingConvention, platform);
}

function isSupportedAssetContent(platform: PLATFORM, content: {
    format: AssetContentFormat;
    density: string;
    densityScale?: number;
}): boolean {
    const { format, density, densityScale = getDensityScale(density) } = content;

    return SUPPORTED_FORMATS[platform].includes(format) &&
        SUPPORTED_DENSITY_SCALES[platform].includes(densityScale);
}

function findAsset<T = Asset>(assets: T[] | unknown, selectedLayer: InspectLayer): {
    asset: T | undefined;
    parent: InspectLayer | null;
} | null {
    if (!Array.isArray(assets)) {
        return null;
    }

    let exportableLayer: InspectLayer | null = selectedLayer;
    while (exportableLayer && !exportableLayer.exportable) {
        exportableLayer = exportableLayer.parent;
    }

    if (!exportableLayer) {
        return null;
    }

    const parent = exportableLayer !== selectedLayer ? exportableLayer : null;
    const asset = assets.find(snapshotAsset => (
        snapshotAsset.layerId
            ? snapshotAsset.layerId === exportableLayer!.sourceId
            : snapshotAsset.layerName === exportableLayer!.name
    ));

    return { asset, parent };
}

export {
    ASSET_FORMAT_TITLES,
    getFormatType,
    getSupportedDensityScales,
    initAssetDensityScalePrefsIfNecessary,
    isAssetDensityScaleSelected,
    getOptimizedAssetsSelectedNames,
    sortFormats,
    formatName,
    getExportName,
    isSupportedAssetContent,
    findAsset
};
